import { LeadPipelineStatus } from "@inrange/building-manager-api-client";
import { Spinner } from "react-bootstrap";
import {
  TONE_COLOUR,
  pipelineStatusText,
  pipelineTone,
} from "./leadPipelineStatus";

// The one render of a lead's contacts-pipeline stage. Three surfaces show it - the
// /leads Status column, the /leads/search Status cell, and the right-hand panel's
// per-role contact-details boxes - and an operator sees them side by side, so a second
// copy of the spinner rule or the empty-case would read as a bug the moment they
// diverged (the lead-pipeline "surfaces that must agree" rule). The wording itself
// comes from the backend via `label`; this file only decides how it looks.
//
// WHICH status a surface should show is a separate question from how it looks, and it
// is not this file's: the status is lead-level, so a per-role box filters it through
// `contactDetailsStatus` first.
export type PipelineStatusVariant =
  // A table cell of its own. An absent status renders a dash, because a blank cell
  // in a column of statuses reads as "still loading" rather than "nothing to say".
  | "cell"
  // Appended to something else that is already on screen - a heading, a badge row.
  // An absent status renders nothing, because the surrounding content carries the
  // row on its own and a dash there would be noise.
  | "inline"
  // Standing in for content that is not there - the contact-details empty state.
  // An absent status renders the generic sentence, because this variant IS the
  // whole of what the operator sees and rendering nothing leaves a blank box.
  | "empty-state";

const NOTHING_PULLED_YET = "No contacts pulled yet";

// Required, not optional-with-a-default: the two variants differ in what they do
// with an absent status, and a caller that forgets to say which it wants would
// silently get the other one's behaviour.
export const PipelineStatusLine: React.FC<{
  status: LeadPipelineStatus | null | undefined;
  variant: PipelineStatusVariant;
}> = ({ status, variant }) => {
  const text = status ? pipelineStatusText(status) : "";
  if (!text) {
    if (variant === "cell") return <span style={{ color: "#adb5bd" }}>-</span>;
    if (variant === "empty-state")
      return (
        <span style={{ fontSize: 12.5, color: "#9aa2ad" }}>
          {NOTHING_PULLED_YET}
        </span>
      );
    return null;
  }
  const tone = pipelineTone(status!);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 12,
        color: TONE_COLOUR[tone],
        // A finished lead is the one state worth picking out of a long column.
        // In the heading the line sits inside uppercased, letter-spaced text, so
        // it resets both and stays at normal weight to read as a subtitle.
        fontWeight: variant === "cell" && tone === "done" ? 600 : 400,
        ...(variant === "cell"
          ? {}
          : { textTransform: "none" as const, letterSpacing: 0 }),
      }}
    >
      {tone === "working" && (
        <Spinner
          animation="border"
          size="sm"
          style={{ width: 10, height: 10 }}
        />
      )}
      {text}
    </span>
  );
};
